// DataTable.jsx — single shared table primitive for the whole app.
//
// Desktop: sortable table, sticky header + sticky first column, a "Columns"
// picker so users choose what's visible (persisted to localStorage per
// tableId). Mobile (<=768px): the same column config renders as a stacked
// card list instead of a horizontally-scrolling table — no page should ever
// hand-roll <table> markup or its own responsive logic again.
//
// Column def shape is TanStack's native ColumnDef, extended via `meta`:
//   { accessorKey, header, cell, enableSorting, size,
//     meta: { mobile, align, mono, truncate } }
//   size:          column width in px (ALWAYS set this — desktop uses
//                  table-layout:fixed so every column needs a deliberate
//                  width, otherwise the browser auto-sizes each column to
//                  its widest cell content, which looks random/uneven)
//   meta.mobile:   'primary' (card title line) | 'secondary' (label:value
//                  row, default) | 'hidden' (desktop-only column)
//   meta.align:    'right' — right-aligns the cell (amounts, counts)
//   meta.mono:     true    — monospace font (IDs, codes)
//   meta.truncate: true    — single-line ellipsis instead of wrapping
//                  (long free-text columns); omit to let content wrap
//                  within the column's fixed width
//
// Sorting is server-side by default in this app (pages pass filters incl.
// sortBy/sortAsc to Supabase) — pass `manualSorting` + controlled `sorting`/
// `onSortingChange` for that. Omit them for small, fully-client-side tables
// and DataTable sorts in-browser instead.
//
// rowStyle(rowOriginal) -> style object, applied to each row (desktop <tr> and
// mobile card) — use for conditional row highlighting (e.g. overdue/stale rows).
//
// isRowExpanded(rowOriginal) -> bool + renderExpandedRow(rowOriginal) -> node:
// pair of props for an inline expand-in-place detail panel (e.g. a "Quick
// view" toggle). The caller owns the expanded-row id in its own state (there's
// no internal expand state here) — DataTable just renders renderExpandedRow's
// output directly under the row when isRowExpanded returns true, as a
// colSpan'd row on desktop or inline in the card on mobile. This is NOT
// row-grouping/tree data — for nested parent+child row structures (collapsible
// groups containing their own sub-rows), DataTable doesn't fit; keep that
// hand-rolled.
//
// Usage:
//   <DataTable tableId="clients" columns={columns} data={clients}
//     getRowId={c => c.id} onRowClick={c => navigate(`/clients/${c.id}`)}
//     sorting={sorting} onSortingChange={handleSortingChange} manualSorting
//     loading={loading} emptyMessage="No clients match your filters." />

import { useState, useEffect, useRef, Fragment } from 'react';
import {
  useReactTable, getCoreRowModel, getSortedRowModel, flexRender,
} from '@tanstack/react-table';
import useIsMobile from '../hooks/useIsMobile';
import { TableSkeleton } from './Skeleton';

const loadVisibility = (tableId) => {
  if (!tableId) return {};
  try { return JSON.parse(localStorage.getItem(`datatable-cols-${tableId}`)) || {}; }
  catch { return {}; }
};

function ColumnsPicker({ tableId, allColumns, columnVisibility, setColumnVisibility }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  useEffect(() => {
    if (!tableId) return;
    localStorage.setItem(`datatable-cols-${tableId}`, JSON.stringify(columnVisibility));
  }, [tableId, columnVisibility]);

  const toggleable = allColumns.filter(c => c.columnDef.meta?.mobile !== 'primary' && c.getCanHide());

  return (
    <div style={{ position: 'relative', flexShrink: 0 }} ref={ref}>
      <button className="btn btn-outline btn-sm" onClick={() => setOpen(o => !o)}>
        ⚙ Columns
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '110%', right: 0, zIndex: 20,
          background: 'var(--card-bg)', border: '0.5px solid var(--border)', borderRadius: 10,
          boxShadow: 'var(--shadow)', padding: 8, minWidth: 190, maxHeight: 320, overflowY: 'auto',
        }}>
          {toggleable.map(col => (
            <label key={col.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', fontSize: 13, color: 'var(--text)', cursor: 'pointer', borderRadius: 6 }}>
              <input
                type="checkbox"
                checked={col.getIsVisible()}
                onChange={col.getToggleVisibilityHandler()}
              />
              {typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// Shared table primitive. Two gotchas the props enforce/document:
//  • maxHeight: pass a concrete height (e.g. "calc(100vh - 300px)") or "none"
//    for page-level scroll. "100%"/"auto" collapse the scroll region and are
//    coerced with a dev warning below.
//  • Sortable columns need an accessorKey or accessorFn — a column with only an
//    `id` + `cell` (computed/derived) can't sort. Give it an accessorFn, or set
//    enableSorting: false to make it explicitly non-sortable.
export default function DataTable({
  tableId,
  columns,
  data,
  getRowId,
  onRowClick,
  sorting: controlledSorting,
  onSortingChange,
  manualSorting = false,
  loading = false,
  skeletonRows = 8,
  skeletonCols = 5,
  emptyMessage = 'Nothing here yet.',
  maxHeight = 'calc(100vh - 260px)',
  showColumnPicker = true,
  rowStyle,
  isRowExpanded,
  renderExpandedRow,
}) {
  // Guard: "100%"/"auto" collapse the scroll region unless every ancestor is a
  // fully height-bounded flex column, so the body clips instead of scrolling.
  // Coerce to a safe concrete height (pass maxHeight="none" for page-level scroll).
  let resolvedMaxHeight = maxHeight;
  if (maxHeight === '100%' || maxHeight === 'auto') {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`DataTable${tableId ? ` [${tableId}]` : ''}: maxHeight="${maxHeight}" doesn't scroll reliably — coercing to "calc(100vh - 260px)". Use a concrete height (e.g. calc(100vh - 300px)) or "none".`);
    }
    resolvedMaxHeight = 'calc(100vh - 260px)';
  }

  const isMobile = useIsMobile();
  const [uncontrolledSorting, setUncontrolledSorting] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState(() => loadVisibility(tableId));

  const sorting = controlledSorting ?? uncontrolledSorting;
  const setSorting = onSortingChange ?? setUncontrolledSorting;

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    manualSorting,
    enableSortingRemoval: false,
    getRowId,
  });

  if (loading) return <TableSkeleton rows={skeletonRows} cols={skeletonCols} />;
  if (!data || data.length === 0) return <div className="empty-state"><p>{emptyMessage}</p></div>;

  // ── Mobile: stacked cards ──────────────────────────────────────────────
  if (isMobile) {
    const primaryCols = columns.filter(c => c.meta?.mobile === 'primary');
    const secondaryCols = columns.filter(c => c.meta?.mobile !== 'primary' && c.meta?.mobile !== 'hidden');
    return (
      <div style={{ maxHeight: resolvedMaxHeight, overflowY: 'auto' }}>
        {table.getRowModel().rows.map(row => (
          <div
            key={row.id}
            onClick={onRowClick ? () => onRowClick(row.original) : undefined}
            style={{ padding: '12px 16px', borderBottom: '0.5px solid var(--border)', cursor: onRowClick ? 'pointer' : 'default', ...(rowStyle ? rowStyle(row.original) : null) }}
          >
            {primaryCols.map(col => {
              const cell = row.getAllCells().find(c => c.column.id === (col.id || col.accessorKey));
              return cell ? (
                <div key={cell.id} style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 4 }}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              ) : null;
            })}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
              {secondaryCols.map(col => {
                const cell = row.getAllCells().find(c => c.column.id === (col.id || col.accessorKey));
                if (!cell) return null;
                const label = typeof col.header === 'string' ? col.header : col.id;
                return (
                  <div key={cell.id} style={{ fontSize: 12, color: 'var(--text3)' }}>
                    <span style={{ marginRight: 5 }}>{label}:</span>
                    <span style={{ color: 'var(--text2)' }}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
                  </div>
                );
              })}
            </div>
            {isRowExpanded && isRowExpanded(row.original) && (
              <div onClick={e => e.stopPropagation()} style={{ marginTop: 10 }}>
                {renderExpandedRow(row.original)}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // ── Desktop: sortable table, sticky header + first column ─────────────
  return (
    <div>
      {showColumnPicker && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 12px', borderBottom: '0.5px solid var(--border)' }}>
          <ColumnsPicker
            tableId={tableId}
            allColumns={table.getAllLeafColumns()}
            columnVisibility={columnVisibility}
            setColumnVisibility={setColumnVisibility}
          />
        </div>
      )}
      <div className="table-wrapper" style={{ maxHeight: resolvedMaxHeight }}>
        <table className="dt-table" style={{ width: table.getTotalSize() }}>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, i) => {
                  const sorted = header.column.getIsSorted();
                  const sticky = i === 0;
                  return (
                    <th
                      key={header.id}
                      className={sticky ? 'dt-sticky-head' : undefined}
                      onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                      style={{
                        width: header.getSize(),
                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        color: sorted ? 'var(--accent)' : undefined,
                        textAlign: header.column.columnDef.meta?.align || 'left',
                        position: sticky ? 'sticky' : undefined,
                        left: sticky ? 0 : undefined,
                        zIndex: sticky ? 2 : undefined,
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <span style={{ marginLeft: 4, opacity: sorted ? 1 : 0.3, fontSize: 10 }}>
                          {sorted === 'desc' ? '▼' : '▲'}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => {
              const expanded = isRowExpanded && isRowExpanded(row.original);
              return (
              <Fragment key={row.id}>
              <tr
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                style={{ cursor: onRowClick ? 'pointer' : 'default', ...(rowStyle ? rowStyle(row.original) : null) }}
              >
                {row.getVisibleCells().map((cell, i) => {
                  const sticky = i === 0;
                  const content = flexRender(cell.column.columnDef.cell, cell.getContext());
                  return (
                    <td
                      key={cell.id}
                      className={sticky ? 'dt-sticky-col' : undefined}
                      style={{
                        width: cell.column.getSize(),
                        fontFamily: cell.column.columnDef.meta?.mono ? 'var(--mono)' : undefined,
                        textAlign: cell.column.columnDef.meta?.align || 'left',
                        position: sticky ? 'sticky' : undefined,
                        left: sticky ? 0 : undefined,
                        zIndex: sticky ? 1 : undefined,
                      }}
                    >
                      {cell.column.columnDef.meta?.truncate
                        ? <span className="dt-ellipsis" title={typeof content === 'string' ? content : undefined}>{content}</span>
                        : content}
                    </td>
                  );
                })}
              </tr>
              {expanded && (
                <tr>
                  <td colSpan={row.getVisibleCells().length} onClick={e => e.stopPropagation()} style={{ cursor: 'default' }}>
                    {renderExpandedRow(row.original)}
                  </td>
                </tr>
              )}
              </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
